import { DestroyRef, Injectable, inject, signal } from '@angular/core';

import { getUUID } from '@dmb/core';

export type PopupMessage = {
  id: string;
  type: 'info' | 'error' | 'warning';
  title: string;
  message?: string;
  timeout?: number;
  action?: {
    buttonText: string;
    callback: () => void;
  };
};

function typeWeight(type: 'info' | 'error' | 'warning') {
  switch (type) {
    case 'error':
      return 1;
    case 'warning':
      return 2;
    case 'info':
      return 3;
  }
}

@Injectable({
  providedIn: 'root',
})
export class PopupCenterService {
  destroyRef = inject(DestroyRef);
  messageList = signal<PopupMessage[]>([]);

  addPopup(message: Omit<PopupMessage, 'id'> & { id?: string }) {
    const newMessage: PopupMessage = { ...message, id: message.id ?? getUUID() };

    this.messageList.update((list) => {
      const newList = [...list, newMessage];
      newList.sort((a, b) => typeWeight(a.type) - typeWeight(b.type));
      return newList;
    });

    if (newMessage.timeout !== undefined) {
      let unregisterCleanup: undefined | (() => void) = undefined;

      const timeoutHandle = setTimeout(() => {
        this.closePopup(newMessage.id);
        unregisterCleanup?.();
      }, newMessage.timeout);

      unregisterCleanup = this.destroyRef.onDestroy(() => {
        clearTimeout(timeoutHandle);
      });
    }
  }

  closePopup(id: string) {
    if (this.messageList().some((message) => message.id === id)) {
      this.messageList.update((list) => list.filter((m) => m.id !== id));
    }
  }

  triggerAction(id: string) {
    const message = this.messageList().find((m) => m.id === id);
    if (message) {
      this.closePopup(id);
      message.action?.callback();
    }
  }
}

export function deleteWithUndo(config: {
  popupCenter: PopupCenterService;
  // defaults to popupCenter's, so the undo window survives the calling component (e.g. a closing dialog)
  destroyRef?: DestroyRef;
  title: string;
  delayMs: number;
  commit: () => void;
  undo: () => void;
  id?: string;
}): void {
  let settled = false;

  const handle = setTimeout(() => settle(config.commit), config.delayMs);

  function settle(fn: () => void) {
    if (settled) {
      return;
    }
    settled = true;
    clearTimeout(handle);
    unregister();
    fn();
  }

  const unregister = (config.destroyRef ?? config.popupCenter.destroyRef).onDestroy(() => settle(config.commit));

  config.popupCenter.addPopup({
    id: config.id,
    type: 'info',
    title: config.title,
    timeout: config.delayMs,
    action: { buttonText: $localize`:@@Common.label.undo:Undo`, callback: () => settle(config.undo) },
  });
}
